<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import ColorOptionsClipboardButtons from '@/components/DesignStudio/ColorOptionsClipboardButtons.vue';
import CSelect, { valueReducer, type CSelectItem } from '@/components/shared/CSelect';
import type { CanvasEx } from '@/typings/canvas';
import type { VectorPlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { ColorObject } from '@/typings/libraries';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { colorsListStore, useColorOptions } from '@/composables/colorOptions';
import { useColorOptionsClipboard } from '@/composables/useColorOptionsClipboard';
import { useToaster } from '@/composables/toaster';
import { DatadogEvent, fireDatadogLog } from '@/utils/datadogUtils';
import { recordFeatureUsage, TrackedFeature } from '@/utils/featureUsage';

const COLOR_TYPE = LibraryTypeEnum.Color;
const DEFAULT_VECTOR_COLOR: ColorObject = { hex: '#000000', alpha: 1, cmyk: '0,0,0,100' };

const vectorTypeOptions: CSelectItem<boolean>[] = [
    {
        label: 'Full Color',
        value: false
    },
    {
        label: 'Single Color',
        value: true
    }
];

const props = defineProps<{
    canvas: CanvasEx;
    vectorElement: VectorPlaceholderElement;
    isSolidBackground?: boolean;
    editMode?: boolean;
    disableColorMode?: boolean;
    readonly?: boolean;
    cmykMode?: boolean;
}>();

const emit = defineEmits<{
    (e: 'open-color-library-selector'): void;
}>();

const vectorMode = ref(false);

const disabled = computed(() => !!props.vectorElement?.locked && !props.isSolidBackground);

const colors = useColorOptions<VectorPlaceholderElement>({
    store: colorsListStore((element, css) => element.set('svgColor', css)),
    element: () => props.vectorElement,
    canvas: () => props.canvas,
    editMode: () => props.editMode,
    openLibrarySelector: () => emit('open-color-library-selector')
});

watch(
    () => props.vectorElement,
    (element) => {
        if (!element || element.starMapConfig) return;

        vectorMode.value = element.applyColoring || props.isSolidBackground || false;
    }
);

const updateVectorMode = () => {
    const element = props.vectorElement;
    if (!element) return;

    element.applyColoring = vectorMode.value;
    if (props.editMode) {
        element.setEditedProp('applyColoring');
    }

    if (!vectorMode.value) {
        colors.removeColorLibrary();
        element.set('svgColor', undefined);
        props.canvas.requestRenderAll();
        return;
    }

    // A single color vector needs at least one option to be painted with
    if (!element.colors?.length) {
        colors.replaceColors(new Map([[1, DEFAULT_VECTOR_COLOR]]), element.colorLibraryId);
        return;
    }

    colors.loadColorIntoElement(1);
};

/* Copy/paste color options, shared with Fill/Stroke/Shadow */
const clipboard = useColorOptionsClipboard();
const toast = useToaster();

const colorSectionLabel = computed(() => (props.isSolidBackground ? 'Solid Bg Color' : 'Vector Color'));

const canCopyPasteVectorColors = computed(() => !disabled.value && !props.readonly);
const canPasteVectorColors = computed(() => {
    const clip = clipboard.clip();
    return canCopyPasteVectorColors.value && !!clip && !clip.hasNonSolidColor;
});

const onCopyVectorColors = () => {
    clipboard.copy({
        colors: new Map(colors.model.colorsMap),
        libraryId: colors.model.libraryId
    });
    fireDatadogLog(DatadogEvent.CopyDsColorOptions);
    toast.show(`${colorSectionLabel.value} options copied`);
};

const onPasteVectorColors = () => {
    const clip = clipboard.clip();
    if (!props.vectorElement || !clip || clip.hasNonSolidColor) return;

    colors.replaceColors(clip.colors, clip.libraryId);
    fireDatadogLog(DatadogEvent.PasteDsColorOptions);
    recordFeatureUsage(TrackedFeature.ColorsPasteVector);
    toast.show(`${colorSectionLabel.value} options pasted`);
};

onMounted(() => {
    vectorMode.value = props?.vectorElement?.applyColoring || !!props.isSolidBackground;
    if (vectorMode.value) {
        colors.loadColorIntoElement(1);
    }
});
</script>

<template>
    <div class="dynamic-vector-properties-wrapper">
        <c-select
            v-if="!props.disableColorMode && !props.isSolidBackground"
            v-model="vectorMode"
            class="mb-2"
            :options="vectorTypeOptions"
            label="Vector type"
            :model-transform="valueReducer"
            :disabled="disabled"
            @input="updateVectorMode"
        />
        <section v-if="vectorMode || props.isSolidBackground" class="mt-3">
            <color-options-clipboard-buttons
                title="Colors"
                :can-copy="canCopyPasteVectorColors"
                :can-paste="canPasteVectorColors"
                @copy="onCopyVectorColors"
                @paste="onPasteVectorColors"
            />
            <div class="border-bottom mt-2" />
            <element-library-selector
                class="selector-wrapper"
                :disabled="disabled"
                :readonly="readonly"
                :element-list="colors.model.colorsMap"
                :library-id="colors.model.libraryId"
                :library-type="COLOR_TYPE"
                :selected-position="colors.model.option"
                :cmyk="cmykMode"
                @option-selected="colors.onColorSelected"
                @load-option="colors.loadColorIntoElement"
                @options-reordered="colors.onColorOptionsReordered"
                @remove-map-option="colors.removeColorOption"
                @remove-library="colors.removeColorLibrary"
                @pick-library="colors.pickColorLibrary"
                @preview-selection="colors.previewColorOption"
            />
        </section>
    </div>
</template>
